import { createId } from './ids';
import { normalizeSettings, type AppSettings } from './settings';
import {
  DISC_FORMATS,
  type Copy,
  type DiscFormat,
  type Movie,
  type Watch,
  type WishlistItem,
} from './types';

/** Bump the trailing number only for a breaking change to the file shape. */
export const EXPORT_SCHEMA = 'physical-movies.export/1';

export interface CollectionExport {
  schema: string;
  exportedAt: string;
  appVersion: string;
  movies: Movie[];
  watches: Watch[];
  wishlist: WishlistItem[];
  /**
   * Optional: an export written before locations existed does not have it.
   * Without it a copy's `locationId` would point at a name nobody can read
   * any more, so the names travel with the discs.
   */
  settings?: AppSettings;
}

export interface BuildExportInput {
  movies: Movie[];
  watches: Watch[];
  wishlist: WishlistItem[];
  settings?: AppSettings;
  appVersion: string;
  now?: Date;
}

export function buildExport(input: BuildExportInput): CollectionExport {
  return {
    schema: EXPORT_SCHEMA,
    exportedAt: (input.now ?? new Date()).toISOString(),
    appVersion: input.appVersion,
    movies: input.movies,
    watches: input.watches,
    wishlist: input.wishlist,
    ...(input.settings ? { settings: input.settings } : {}),
  };
}

export function exportFilename(now: Date = new Date()): string {
  const stamp = now.toISOString().slice(0, 10);
  return `physical-movies-${stamp}.json`;
}

/** Thrown when the file is not an export at all. Bad *entries* are skipped. */
export class ImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImportError';
  }
}

export interface ParsedImport {
  movies: Movie[];
  watches: Watch[];
  wishlist: WishlistItem[];
  /** Null when the file predates locations, or carried none. */
  settings: AppSettings | null;
  /** Entries that were dropped because they were unreadable. */
  skipped: number;
}

type Raw = Record<string, unknown>;

const isObject = (value: unknown): value is Raw =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const num = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const isoDate = (value: unknown, fallback: string): string => {
  const raw = str(value);
  if (!raw) return fallback;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? fallback : date.toISOString();
};

const format = (value: unknown): DiscFormat => {
  const raw = str(value);
  return raw && (DISC_FORMATS as readonly string[]).includes(raw) ? (raw as DiscFormat) : 'hd';
};

function parseCopy(raw: unknown): Copy | null {
  if (!isObject(raw)) return null;
  const loanRaw = raw.loan;
  const loan =
    isObject(loanRaw) && str(loanRaw.to)
      ? {
          to: str(loanRaw.to) as string,
          since: (str(loanRaw.since) ?? new Date().toISOString()).slice(0, 10),
          note: str(loanRaw.note),
        }
      : null;

  return {
    id: str(raw.id) ?? createId(),
    format: format(raw.format),
    edition: str(raw.edition),
    notes: str(raw.notes),
    purchasedAt: str(raw.purchasedAt),
    loan,
  };
}

function parseMovie(raw: unknown, now: string): Movie | null {
  if (!isObject(raw)) return null;
  const id = str(raw.id);
  const title = str(raw.title);
  if (!id || !title) return null;

  const copies = Array.isArray(raw.copies)
    ? raw.copies.map(parseCopy).filter((copy): copy is Copy => copy !== null)
    : [];

  const genreIds = Array.isArray(raw.genreIds)
    ? raw.genreIds.map(num).filter((value): value is number => value !== null)
    : [];

  const directors = Array.isArray(raw.directors)
    ? raw.directors.map(str).filter((value): value is string => value !== null)
    : [];

  return {
    id,
    tmdbId: num(raw.tmdbId),
    title,
    titleDe: str(raw.titleDe),
    originalTitle: str(raw.originalTitle),
    year: num(raw.year),
    releaseDate: str(raw.releaseDate),
    runtime: num(raw.runtime),
    posterPath: str(raw.posterPath),
    backdropPath: str(raw.backdropPath),
    overviewEn: str(raw.overviewEn),
    overviewDe: str(raw.overviewDe),
    genreIds,
    directors,
    copies,
    personalNote: str(raw.personalNote),
    addedAt: isoDate(raw.addedAt, now),
    updatedAt: isoDate(raw.updatedAt, now),
    watchCount: Math.max(0, Math.round(num(raw.watchCount) ?? 0)),
    lastWatchedAt: str(raw.lastWatchedAt),
  };
}

function parseWatch(raw: unknown, now: string): Watch | null {
  if (!isObject(raw)) return null;
  const id = str(raw.id);
  const movieId = str(raw.movieId);
  if (!id || !movieId) return null;

  const rating = num(raw.rating);

  return {
    id,
    movieId,
    watchedAt: isoDate(raw.watchedAt, now),
    rating: rating === null ? null : Math.min(5, Math.max(1, Math.round(rating))),
    note: str(raw.note),
    runtimeMinutes: num(raw.runtimeMinutes),
    createdAt: isoDate(raw.createdAt, now),
  };
}

function parseWishlistItem(raw: unknown, now: string): WishlistItem | null {
  if (!isObject(raw)) return null;
  const id = str(raw.id);
  const title = str(raw.title);
  if (!id || !title) return null;

  const desired = str(raw.desiredFormat);

  return {
    id,
    tmdbId: num(raw.tmdbId),
    title,
    titleDe: str(raw.titleDe),
    year: num(raw.year),
    releaseDate: str(raw.releaseDate),
    posterPath: str(raw.posterPath),
    runtime: num(raw.runtime),
    desiredFormat:
      desired && (DISC_FORMATS as readonly string[]).includes(desired)
        ? (desired as DiscFormat)
        : null,
    genreIds: Array.isArray(raw.genreIds)
      ? raw.genreIds.map(num).filter((value): value is number => value !== null)
      : [],
    // Older exports predate the flag; those entries were all buyable.
    buyable: typeof raw.buyable === 'boolean' ? raw.buyable : true,
    deletedAt: str(raw.deletedAt),
    note: str(raw.note),
    addedAt: isoDate(raw.addedAt, now),
  };
}

/**
 * Read an export file.
 *
 * Structural problems throw; individual unreadable entries are skipped and
 * counted, so one corrupt row never costs you the whole import.
 */
export function parseExport(input: unknown, now: Date = new Date()): ParsedImport {
  let data = input;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      throw new ImportError('not valid JSON');
    }
  }

  if (!isObject(data)) throw new ImportError('not an object');

  const schema = str(data.schema);
  if (!schema) throw new ImportError('missing a schema field');
  if (!schema.startsWith('physical-movies.export/')) {
    throw new ImportError(`unknown schema "${schema}"`);
  }

  const version = Number(schema.split('/')[1]);
  if (!Number.isFinite(version) || version > 1) {
    throw new ImportError(`schema version ${schema.split('/')[1]} is newer than this app`);
  }

  const stamp = now.toISOString();
  let skipped = 0;

  const take = <T>(value: unknown, parse: (raw: unknown) => T | null): T[] => {
    if (value === undefined || value === null) return [];
    if (!Array.isArray(value)) throw new ImportError('movies, watches and wishlist must be lists');
    const result: T[] = [];
    for (const entry of value) {
      const parsed = parse(entry);
      if (parsed === null) skipped += 1;
      else result.push(parsed);
    }
    return result;
  };

  const movies = take(data.movies, (raw) => parseMovie(raw, stamp));
  const watches = take(data.watches, (raw) => parseWatch(raw, stamp));
  const wishlist = take(data.wishlist, (raw) => parseWishlistItem(raw, stamp));

  if (movies.length === 0 && watches.length === 0 && wishlist.length === 0) {
    throw new ImportError('it contains no entries');
  }

  // Only meaningful if it actually names somewhere; an empty list is the
  // same as not having been exported at all.
  const parsedSettings = normalizeSettings(data.settings ?? null);
  const settings = parsedSettings.locations.length > 0 ? parsedSettings : null;

  return { movies, watches, wishlist, settings, skipped };
}
